if (!customElements.get('currency-checkout-modal')) {
  class CurrencyCheckoutModal extends HTMLElement {
    constructor() {
      super();
      this.workerUrl = (this.dataset.workerUrl || '').replace(/\/$/, '');
      this.selectedCurrency = 'BRL';
      this.pendingForm = null;
      this.pendingButton = null;
      this.rate = null;
      this.cartTotalCents = null;
    }

    connectedCallback() {
      this.querySelectorAll('[data-currency-close]').forEach((el) => {
        el.addEventListener('click', (event) => {
          if (event.target === el) this.close();
        });
      });

      this.querySelectorAll('[data-currency-option]').forEach((button) => {
        button.addEventListener('click', () => this.selectCurrency(button.dataset.currencyOption));
      });

      const confirmButton = this.querySelector('[data-currency-confirm]');
      if (confirmButton) {
        confirmButton.addEventListener('click', () => this.confirm());
      }

      const retryButton = this.querySelector('[data-currency-retry]');
      if (retryButton) {
        retryButton.addEventListener('click', () => this.confirm());
      }

      const payBrlButton = this.querySelector('[data-currency-pay-brl]');
      if (payBrlButton) {
        payBrlButton.addEventListener('click', () => {
          this.selectCurrency('BRL');
          this.submitBrl();
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
    }

    async open(form, button) {
      this.pendingForm = form;
      this.pendingButton = button;
      this.selectCurrency('BRL');
      this.setStep('select');
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));

      const brlAmountEl = this.querySelector('[data-currency-amount-brl]');
      const usdAmountEl = this.querySelector('[data-currency-amount-usd]');
      if (brlAmountEl) brlAmountEl.textContent = '…';
      if (usdAmountEl) usdAmountEl.textContent = '…';

      await this.refreshCartTotal();
      this.fetchRate();
    }

    close() {
      this.classList.remove('is-open');
      setTimeout(() => {
        this.hidden = true;
      }, 250);
    }

    async refreshCartTotal() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error('cart request failed');
        const cart = await response.json();
        this.cartTotalCents = Number(cart.total_price || 0);

        const brlAmountEl = this.querySelector('[data-currency-amount-brl]');
        if (brlAmountEl) brlAmountEl.textContent = this.formatBrl(this.cartTotalCents);

        this.updateUsdAmount();
      } catch (error) {
        console.error('[currency-checkout] failed to load cart total', error);
      }
    }

    formatBrl(cents) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
    }

    async fetchRate() {
      if (this.rate || !this.workerUrl) {
        this.updateUsdAmount();
        return;
      }
      try {
        const response = await fetch(`${this.workerUrl}/rate`);
        if (!response.ok) throw new Error('rate request failed');
        const data = await response.json();
        this.rate = Number(data.rate);
        this.updateUsdAmount();
      } catch (error) {
        console.error('[currency-checkout] failed to load exchange rate', error);
      }
    }

    updateUsdAmount() {
      const amountEl = this.querySelector('[data-currency-amount-usd]');
      if (!amountEl || !this.rate || this.cartTotalCents === null) return;
      const usdValue = (this.cartTotalCents / 100) / this.rate;
      amountEl.textContent = `≈ US$ ${usdValue.toFixed(2).replace('.', ',')}`;
    }

    selectCurrency(currency) {
      this.selectedCurrency = currency;
      this.querySelectorAll('[data-currency-option]').forEach((button) => {
        button.classList.toggle('selected', button.dataset.currencyOption === currency);
      });
      const confirmButton = this.querySelector('[data-currency-confirm]');
      if (confirmButton) {
        confirmButton.textContent = currency === 'USD' ? 'Continuar em Dólar' : 'Continuar em Real';
      }
    }

    setStep(step) {
      this.querySelectorAll('[data-currency-step]').forEach((panel) => {
        panel.hidden = panel.dataset.currencyStep !== step;
      });
    }

    confirm() {
      if (this.selectedCurrency === 'USD') {
        this.createUsdOrder();
      } else {
        this.submitBrl();
      }
    }

    submitBrl() {
      this.close();
      if (this.pendingForm) this.pendingForm.requestSubmit(this.pendingButton || undefined);
    }

    async createUsdOrder() {
      this.setStep('processing');
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const items = cart.items.map((item) => {
          const rowWithLabel = document.querySelector(
            `[data-currency-key="${CSS.escape(item.key)}"][data-currency-variant-label]:not([data-currency-variant-label=""])`
          );
          const variantLabel = rowWithLabel ? rowWithLabel.dataset.currencyVariantLabel : '';

          return {
            variantId: item.id,
            variantLabel: variantLabel || '',
            productTitle: item.product_title || '',
          };
        });

        const orderResponse = await fetch(`${this.workerUrl}/create-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cartToken: cart.token, items }),
        });

        if (!orderResponse.ok) throw new Error(`create-order failed with status ${orderResponse.status}`);

        const data = await orderResponse.json();
        if (!data.invoiceUrl) throw new Error('missing invoiceUrl in response');

        window.location.href = data.invoiceUrl;
      } catch (error) {
        console.error('[currency-checkout] failed to create USD order', error);
        this.setStep('error');
      }
    }
  }

  customElements.define('currency-checkout-modal', CurrencyCheckoutModal);
}

document.addEventListener('DOMContentLoaded', () => {
  const modal = document.getElementById('CurrencyCheckoutModal');
  if (!modal) return;

  const checkoutButtonIds = ['CartDrawer-Checkout', 'checkout'];

  document.addEventListener('click', (event) => {
    const button = checkoutButtonIds
      .map((id) => event.target.closest(`#${id}`))
      .find((match) => match);
    if (!button) return;

    const formId = button.getAttribute('form');
    const form = formId ? document.getElementById(formId) : button.closest('form');
    if (!form) return;

    event.preventDefault();

    modal.open(form, button);
  });
});
